import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useDispatch, useSelector } from 'react-redux';

import {
  selectActiveFundingSourceOperation,
  selectClient,
  selectSession,
  selectSessionAdvisor,
} from '@client/model/selectors';
import { putMultipleFundingSourceForClientByClientIdRequest } from '@client/model';
import { SessionType } from '@shared';

import {
  ASSET_TYPES,
  ASSET_TYPE_TO_ACCOUNT_TYPE,
  type AssetTypeId,
} from '@/features/guided-financial-planning/config';

// ─────────────────────────────────────────────────────────────
// SCHEMA
// ─────────────────────────────────────────────────────────────

const selfFundingFormSchema = z.object({
  existingAssetsValue: z.number({ required_error: 'Required' }).min(0, 'Must be 0 or greater'),
  yearlyContribution: z.number().nullable(),
  annualRateOfReturn: z.number({ required_error: 'Required' }),
  gainsTaxRate: z.number({ required_error: 'Required' }),
  contributionStopYear: z.number().nullable().optional(),
  hasDrawdown: z.boolean().default(false),
  drawdownStartAtYear: z.number().nullable().optional(),
  drawdownEndAtYear: z.number().nullable().optional(),
  drawdownAmount: z.number().min(0).nullable().optional(),
  drawdownAmountInterval: z.enum(['monthly', 'yearly']).default('yearly'),
});

export type SelfFundingFormValues = z.infer<typeof selfFundingFormSchema>;

export function isSelfFundingFormSavable(values: unknown): boolean {
  return selfFundingFormSchema.safeParse(values).success;
}

// ─────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────

function getAssetLabel(assetTypeId: AssetTypeId): string {
  return ASSET_TYPES.find(asset => asset.id === assetTypeId)?.label ?? assetTypeId;
}

// ─────────────────────────────────────────────────────────────
// HOOK
// ─────────────────────────────────────────────────────────────

export function useSelfFundingForm(
  assetTypeId: AssetTypeId,
  fundingSourceId: string | null,
  existingSource?: SelfFundingSource,
) {
  const client = useSelector(selectClient);
  const { sessionType } = useSelector(selectSession);
  const { advisorId } = useSelector(selectSessionAdvisor);
  const activeFundingSourceOperation = useSelector(selectActiveFundingSourceOperation);

  const clientId = client.clientId;
  const inference = client.appliedInferenceSet;
  const ltcAtYear =
    inference?.ltcAtYear ??
    (inference?.yearsTillLtc != null
      ? new Date().getFullYear() + Math.max(0, Math.round(inference.yearsTillLtc))
      : new Date().getFullYear() + 30);

  const isAdvisorSession =
    sessionType === SessionType.advisor || sessionType === SessionType.wizard;

  const isRetirementIncome = assetTypeId === 'retirement-income';

  const dispatch = useDispatch();

  // Track if this specific funding source is being saved
  const isSaving =
    activeFundingSourceOperation !== null &&
    activeFundingSourceOperation.fundingSourceId === fundingSourceId;

  const form = useForm<SelfFundingFormValues>({
    resolver: zodResolver(selfFundingFormSchema),
    mode: 'onSubmit',
    defaultValues: {
      existingAssetsValue: existingSource?.existingAssetsValue ?? undefined,
      yearlyContribution: isRetirementIncome
        ? null
        : existingSource?.monthlyContributionAmount
          ? existingSource.monthlyContributionAmount * 12
          : null,
      annualRateOfReturn: isRetirementIncome ? 0 : (existingSource?.annualRateOfReturn ?? undefined),
      gainsTaxRate: existingSource?.gainsTaxRate ?? undefined,
      contributionStopYear: existingSource?.contributionStopYear ?? null,
      hasDrawdown: !!existingSource?.hasDrawdown,
      drawdownStartAtYear: existingSource?.drawdownStartAtYear ?? null,
      drawdownEndAtYear: existingSource?.drawdownEndAtYear ?? null,
      drawdownAmount: existingSource?.drawdownAmount ?? null,
      drawdownAmountInterval: existingSource?.drawdownAmountInterval ?? 'yearly',
    },
  });

  // Reset form when fundingSourceId or existingSource changes
  useEffect(() => {
    form.reset({
      existingAssetsValue: existingSource?.existingAssetsValue ?? undefined,
      yearlyContribution: isRetirementIncome
        ? null
        : existingSource?.monthlyContributionAmount
          ? existingSource.monthlyContributionAmount * 12
          : null,
      annualRateOfReturn: isRetirementIncome
        ? 0
        : (existingSource?.annualRateOfReturn ?? undefined),
      gainsTaxRate: existingSource?.gainsTaxRate ?? undefined,
      contributionStopYear: existingSource?.contributionStopYear ?? null,
      hasDrawdown: !!existingSource?.hasDrawdown,
      drawdownStartAtYear: existingSource?.drawdownStartAtYear ?? null,
      drawdownEndAtYear: existingSource?.drawdownEndAtYear ?? null,
      drawdownAmount: existingSource?.drawdownAmount ?? null,
      drawdownAmountInterval: existingSource?.drawdownAmountInterval ?? 'yearly',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fundingSourceId, existingSource]);

  const buildSourceData = useCallback(
    (data: SelfFundingFormValues): SelfFundingSource => {
      const accountType = ASSET_TYPE_TO_ACCOUNT_TYPE[assetTypeId];
      const accountName = getAssetLabel(assetTypeId);

      if (isRetirementIncome) {
        // Retirement income: existingAssetsValue = annual income,
        // gainsTaxRate = income tax rate. No growth or contribution fields.
        return {
          hasSelfFunding: true,
          selfFundingName: existingSource?.selfFundingName ?? accountName,
          accountType,
          existingAssetsValue: data.existingAssetsValue,
          annualRateOfReturn: 0,
          contributionStartYear: new Date().getFullYear(),
          contributionEndYear: ltcAtYear,
          monthlyContributionAmount: 0,
          gainsTaxRate: data.gainsTaxRate,
          monthlyIncome: null,
          contributionMonthCount: 0,
          ...(isAdvisorSession && { addedByAdvisorId: advisorId }),
        } as SelfFundingSource;
      }

      return {
        hasSelfFunding: true,
        selfFundingName: existingSource?.selfFundingName ?? accountName,
        accountType,
        existingAssetsValue: data.existingAssetsValue,
        annualRateOfReturn: data.annualRateOfReturn,
        contributionStartYear: new Date().getFullYear(),
        contributionEndYear: ltcAtYear,
        monthlyContributionAmount: (data.yearlyContribution ?? 0) / 12,
        gainsTaxRate: data.gainsTaxRate,
        monthlyIncome: null,
        contributionMonthCount: 0,
        contributionStopYear: data.contributionStopYear ?? null,
        hasDrawdown: !!data.hasDrawdown,
        drawdownStartAtYear: data.hasDrawdown ? (data.drawdownStartAtYear ?? null) : null,
        drawdownEndAtYear: data.hasDrawdown ? (data.drawdownEndAtYear ?? null) : null,
        drawdownAmount: data.hasDrawdown ? (data.drawdownAmount ?? null) : null,
        drawdownAmountInterval: data.hasDrawdown ? (data.drawdownAmountInterval ?? 'yearly') : null,
        ...(isAdvisorSession && { addedByAdvisorId: advisorId }),
      } as SelfFundingSource;
    },
    [assetTypeId, isRetirementIncome, existingSource?.selfFundingName, ltcAtYear, isAdvisorSession, advisorId],
  );

  const onSubmit = useCallback(
    (data: SelfFundingFormValues) => {
      const updatedSelfFunding = buildSourceData(data);

      dispatch(
        putMultipleFundingSourceForClientByClientIdRequest({
          clientId,
          fundingSourceType: 'selfFunding',
          fundingSourceData: updatedSelfFunding,
          fundingSourceId: fundingSourceId ?? null,
        }),
      );
    },
    [buildSourceData, clientId, fundingSourceId, dispatch],
  );

  return {
    form,
    onSubmit,
    buildSourceData,
    isSaving,
    clientId,
    isRetirementIncome,
  };
}
